import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    effect,
    signal,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DataRowPlaceholderService } from '@app/core/services/data-row-placeholder.service';
import { DateHelperService } from '@app/core/services/date-helper-service';
import { LoggingService } from '@app/core/services/logging.service';
import { TenantStateService } from '@app/core/services/tenant-state.service';
import { UploadSessionDto } from '@app/core/services/create-session.service';
import {
    SessionInfo,
    SessionInfoStateStoreService,
} from '@app/core/services/session-info-state-store.service';
import { AppSettingsStore } from '@app/core/state/app-settings-store/app-settings-store';
import { ToastMessageOptions } from '@common/models';
import { DownloadService } from '@modules/app-common/services/download.service';
import { ToastService } from '@modules/app-common/services/toast.service';
import { AuthService } from '@modules/auth/services/auth.service';
import { MyColumn } from '@modules/custom-table/models';
import { DocType } from '@app/core/models/doc-type.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
    AsyncSettingsModel,
    RemovingEventArgs,
    SelectedEventArgs,
    UploaderComponent,
} from '@syncfusion/ej2-angular-inputs';
import { EmitType } from '@syncfusion/ej2-base';
import { take } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { UpdateSessionService } from '../../../reconciliation-manager/services/update-session.service';
import { AuthStore } from '@app/core/state/auth.store';
import { TimezoneStore } from '@app/core/state/timezone-store/timezone-store.service';
import { TimezoneConverterService } from '@app/core/services/timezone-converter.service';

@Component({
    selector: 'cwr-file-upload',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './file-upload.component.html',
    styleUrls: ['file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, AfterViewInit, OnChanges {
    @ViewChild('defaultUpload') uploadObj!: UploaderComponent;
    @ViewChild('dropTargetRef') dropTargetRef!: ElementRef;
    @ViewChild('dropAreaRef') dropAreaRef!: ElementRef;
    @Input() modal: NgbModalRef | null = null;
    @Input() sessionId = '';
    @Input() isCreatingNewSession = false;
    @Input() isAddingFiles = false;
    @Input() newSession!: UploadSessionDto;
    @Input() currentClientId: string | null = null;
    @Input() sessionCreatedDate: string | null = null; // expected to be UTC ISO string
    @Input() sessionDocType: string | null = null;
    @Input() sessionSupplierStatementsSelected = false;
    @Input() selectedSessionId: string | null = null;
    @Input() isReadOnly = false;
    @Input() hideSessionMeta = false;
    @Input() showPdfIcon = true;
    @Input() showTxtIcon = true;
    @Input() showExcelIcon = true;
    @Input() maxFiles: number | null = null;
    @Input() isErpUpload = false;
    @Input() erpModalTitle = 'Add ERP Files';
    @Input() confirmButtonText = 'OK';
    @Input() hideCancelButton = false;
    @Input() hideNotes = false;
    @Input() showQueueHeading = true;
    get isRegistered(): boolean {
        return this.appSettingsStore.isRegistered();
    }

    public uploaderSettings: AsyncSettingsModel = {
        saveUrl: '',
        removeUrl: '',
        chunkSize: 0,
        retryCount: 0,
        retryAfterDelay: 0
    };

    public allowExtensions = '.pdf,.csv,.xls,.xlsx';
    isLoading = false;
    private guid = '';
    private isRemoving = false;

    showStartNewButton = false;
    options: ToastMessageOptions = {};
    description = '';

    cols: MyColumn[] = [];

    notes = '';
    isFocused = false;
    placeholder = 'Add Notes';
    isSupplierStatementsSelected = true;
    pendingFilesCount = signal<number>(0);
    maxFilesError = '';

    get currentTenantSlug(): string {
        return this.authStore.getClientId() || 'N/A';
    }

    isUploading = false;
    uploadedFilesCount = 0;
    totalFilesToUpload = 0;
    uploadTriggeredByOkButton = false;

    sessionInfo: SessionInfo = {
        sessionId: '',
        sessionClientId: '',
        sessionDate: '',
        sessionDateUtc: '',
        sessionUser: '',
        sessionTimeZoneIana: undefined,
        sessionOffsetMinutes: null,
    };
    sessionCreatedDateDisplay = '';

    constructor(
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private http: HttpClient,
        private toastService: ToastService,
        private downloadService: DownloadService,
        private sessionService: SessionInfoStateStoreService,
        private dateHelper: DateHelperService,
        private updateSessionService: UpdateSessionService,
        private appSettingsStore: AppSettingsStore,
        private placeholderService: DataRowPlaceholderService,
        private loggingService: LoggingService,
        private authStore: AuthStore,
        private timezoneConverterService: TimezoneConverterService,
        private tenantStateService: TenantStateService,
        private timezoneStore: TimezoneStore

    ) {
        effect(() => {
            const backendSettings = this.appSettingsStore.uploaderSettings();
            if (backendSettings) {
                this.uploaderSettings = {
                    ...this.uploaderSettings,
                    chunkSize: backendSettings.chunkSizeMB * 1024 * 1024,
                    retryCount: backendSettings.retryCount,
                    retryAfterDelay: backendSettings.retryAfterDelayMs,
                    removeUrl: backendSettings.removeUrl
                };

                this.loggingService.debug('[FileUpload] Uploader settings updated from AppSettingsStore:', this.uploaderSettings);
            } else {
                this.loggingService.debug('[FileUpload] No uploader settings available in AppSettingsStore yet');
            }
        });
    }

    ngOnInit() {
        this.setupUploadUrl();
        if (this.isErpUpload) {
            this.notes = '';
            this.isFocused = false;
        }

        if (!this.isCreatingNewSession) {
            const activeSession = this.sessionService.getActiveUploadSessionValue();

            if (activeSession) {
                if (activeSession.docType) {
                    this.isSupplierStatementsSelected = this.resolveDocTypeFromSession(activeSession.docType) === DocType.Statement;
                }
                this.sessionInfo = {
                    sessionId: activeSession.id,
                    sessionClientId: this.authStore.getClientValue()  || '',
                    sessionDate: activeSession.createdDate,
                    sessionDateUtc: activeSession.createdDateUtc || activeSession.createdDate,
                    sessionUser: activeSession.user,
                };

                const finalSessionId = this.isCreatingNewSession
                    ? this.sessionId
                    : this.sessionInfo.sessionId;
                const finalDateUtc = this.isCreatingNewSession
                    ? new Date().toISOString()
                    : (this.sessionInfo.sessionDateUtc || this.sessionInfo.sessionDate);

                this.currentClientId = this.sessionInfo.sessionClientId;
                this.selectedSessionId = finalSessionId;
                this.sessionCreatedDate = finalDateUtc;
                this.sessionCreatedDateDisplay = activeSession.createdDate || this.sessionCreatedDateDisplay;

                this.loggingService.debug('Session Info:', this.sessionInfo);
                this.loggingService.debug('Final Values:', {
                    sessionId: finalSessionId,
                    sessionDateUtc: finalDateUtc,
                    clientId: this.currentClientId,
                });

                this.setAddFileUrl(finalSessionId, finalDateUtc, activeSession.createdDate);

                this.sessionId = this.sessionId || this.sessionInfo.sessionId;

                if (this.isAddingFiles) {
                    this.notes = activeSession.notes || '';
                }
            }
        }
    }

    ngAfterViewInit() {
        this.uploadObj.dropArea = this.dropTargetRef.nativeElement;
    }

    ngOnChanges(changes: SimpleChanges): void {
        const sessionChange = changes['sessionId'];
        const createdDateChange = changes['sessionCreatedDate'];
        const newSessionChange = changes['newSession'];

        if (newSessionChange?.currentValue) {
            if (newSessionChange.currentValue.docType) {
                this.isSupplierStatementsSelected = this.resolveDocTypeFromSession(newSessionChange.currentValue.docType) === DocType.Statement;
            }
            const finalSessionId = this.isCreatingNewSession
                ? (this.sessionId || newSessionChange.currentValue.sessionId || this.sessionInfo.sessionId)
                : (this.sessionInfo.sessionId || this.sessionId || newSessionChange.currentValue.sessionId);

            let finalDateUtc =
                this.sessionCreatedDate ||
                newSessionChange.currentValue.createDateUtc ||
                newSessionChange.currentValue.createDate ||
                this.sessionInfo?.sessionDateUtc ||
                this.sessionInfo?.sessionDate ||
                new Date().toISOString();

            this.loggingService.debug('date', finalDateUtc, 'new SESSION fLAG', this.isCreatingNewSession);

            const displayText =
                newSessionChange.currentValue.displayCreateDate ||
                newSessionChange.currentValue.createDate ||
                this.sessionInfo?.sessionDate ||
                this.sessionCreatedDateDisplay;

            if (displayText) {
                this.sessionCreatedDateDisplay = displayText;
            }

            if (finalSessionId) {
                this.setAddFileUrl(finalSessionId, finalDateUtc, displayText);
            }
        } else if (createdDateChange?.currentValue || sessionChange?.currentValue) {
            const finalSessionId = this.sessionId || this.sessionInfo.sessionId;
            const finalDateUtc =
                this.sessionCreatedDate ||
                this.sessionInfo?.sessionDateUtc ||
                this.sessionInfo?.sessionDate ||
                new Date().toISOString();

            if (finalSessionId) {
                this.setAddFileUrl(finalSessionId, finalDateUtc, this.sessionCreatedDateDisplay);
            }
        }

        this.setupUploadUrl();
    }

    private setupUploadUrl(): void {
        const id = this.computeSessionId();
        const rawDateUtc = this.computeSessionDate();

        if (!id || !rawDateUtc) {
            this.loggingService.debug('[Uploader] Skipping upload URL setup. Missing id/date', { id, rawDateUtc });
            return;
        }

        this.applyUploadUrl(id, rawDateUtc, this.sessionCreatedDateDisplay);
    }

    private computeSessionId(): string {
        return this.sessionId || this.sessionService.getSessionInfo().sessionId;
    }

    private computeSessionDate(): string | null {
        return this.sessionCreatedDate;
    }

    /**
     * Apply upload URL with timezone conversion to ensure consistent folder creation
     * across all timezones (AU, USA, China, PH, etc.)
     *
     * `lastUpdatedAt` is sent as a UTC ISO timestamp so the backend can persist
     * the most recent file-add activity using an unambiguous date-time value.
     */
    private applyUploadUrl(sessionId: string, rawDate: Date | string, displayText?: string): void {
        this.timezoneConverterService
            .ensureUtcIsoString(rawDate)
            .pipe(take(1))
            .subscribe({
                next: (utcDate: string) => {
                    const formattedDate = this.dateHelper.formatStringDateForQuery(utcDate);
                    const lastUpdatedAt = new Date().toISOString();
                    const url =
                        `${environment.apiRoot}sessions/${encodeURIComponent(sessionId)}/files` +
                        `?sessionDate=${encodeURIComponent(formattedDate)}` +
                        `&lastUpdatedAt=${encodeURIComponent(lastUpdatedAt)}`;

                    this.sessionCreatedDate = utcDate;
                    this.updateSessionCreatedDateDisplay(utcDate, displayText);
                    this.uploaderSettings = {
                        ...this.uploaderSettings,
                        saveUrl: url
                    };
                    this.loggingService.debug('[Uploader] saveUrl', url);
                    this.cdr.markForCheck();
                },
                error: (error: any) => this.loggingService.error('[Uploader] Failed to compute upload URL', error)
            });
    }

    private setAddFileUrl(sessionId: string, dateValue: Date | string, displayText?: string): void {
        this.applyUploadUrl(sessionId, dateValue, displayText);
    }

    private updateSessionCreatedDateDisplay(utcDate: string, displayText?: string): void {
        const tenantZone = this.appSettingsStore.currentTimeZone();

        if (displayText) {
            this.sessionCreatedDateDisplay = displayText.trim();
            return;
        }

        this.sessionCreatedDateDisplay = this.timezoneConverterService.formatUtcToLocal(utcDate, tenantZone);
    }

    public onBeforeSend: EmitType<Object> = (args: any) => {
        const user = this.authService.getUser();

        if (!user || user.expired) {
            this.loggingService.error('[FileUpload] Token expired during upload. User needs to re-login.');
            this.toastService.showError('Session expired. Please refresh the page and login again.');
            args.cancel = true;
            return;
        }

        const token = this.authService.getAccessToken();

        if (!token) {
            this.loggingService.error('[FileUpload] No access token available.');
            this.toastService.showError('Authentication error. Please refresh and login again.');
            args.cancel = true;
            return;
        }

        this.loggingService.debug('[FileUpload] Setting Authorization header with fresh token (length:', token.length, ')');
        args.currentRequest.setRequestHeader('Authorization', `Bearer ${token}`);

        const appSettings = this.appSettingsStore.settings();
        let clientId: string = appSettings?.user?.clientId || '';
        let sessUser: string = appSettings?.user?.id || '';

        const tenantId = this.tenantStateService.getCurrentTenantId();
        if (!tenantId) {
            this.loggingService.error('[FileUpload] Missing tenant ID; upload request may fail.');
        }

        this.loggingService.debug('Setting headers - ClientId:', clientId, 'User:', sessUser, 'TenantId:', tenantId);
        args.currentRequest.setRequestHeader('X-ClientId', clientId);
        args.currentRequest.setRequestHeader('X-User', sessUser);
        if (tenantId) {
            args.currentRequest.setRequestHeader('X-Tenant-Id', tenantId);
        }
    };

    public onUploadSuccess: EmitType<Object> = (args: any) => {
        this.loggingService.info(`File uploaded successfully: ${args.file?.name}`);

        this.uploadedFilesCount++;

        if (this.totalFilesToUpload === 0) {
            const allFiles = this.uploadObj.getFilesData();
            const uploadingOrUploaded = allFiles.filter(f =>
                f.status === 'File uploaded successfully' ||
                f.statusCode === '2' ||
                f.status === 'Uploading'
            ).length;
            this.totalFilesToUpload = uploadingOrUploaded;
        }

        this.updatePendingFilesCount();

        if (this.uploadedFilesCount === this.totalFilesToUpload && this.totalFilesToUpload > 0) {
            this.loggingService.info('All files uploaded.');
            this.isUploading = false;

            if (this.uploadTriggeredByOkButton) {
                if (this.isErpUpload) {
                    this.toastService.showSuccess(`${this.erpModalTitle} uploaded.`);
                    this.closeContainer();
                } else if (this.isCreatingNewSession) {
                    this.executeUpdateSession();
                } else {
                    this.appSettingsStore.setShouldRefreshTables();
                    this.closeContainer();
                }
                this.uploadTriggeredByOkButton = false;
            } else {
                this.cdr.markForCheck();
            }
        }
    };

    public onFilesCleared(args: any): void {
        this.pendingFilesCount.set(0);
        this.maxFilesError = '';
    }

    private updatePendingFilesCount(args?: SelectedEventArgs): void {

        const files = this.uploadObj.getFilesData() || [];
        const readyCount = files.filter((f) => f.status === 'Ready to upload').length;
        this.pendingFilesCount.set(readyCount);
    }
    public onFileSelected(args: SelectedEventArgs): void {
        this.loggingService.debug('\n\n!!!!Selected file', args);
        /**
         * TEMPORARY (ERP modal only):
         * Enforce a strict max file count until backend validation is added.
         * If the user tries to exceed the limit, we block the selection and
         * show an inline message. This keeps the UI consistent with the
         * upcoming server-side rule without impacting other flows.
         */
        if (this.maxFiles && this.maxFiles > 0) {
            const existing = this.uploadObj?.getFilesData()?.length ?? 0;
            const incoming = args?.filesData?.length ?? 0;
            if (existing + incoming > this.maxFiles) {
                this.maxFilesError = `You can upload a maximum of ${this.maxFiles} files.`;
                if (args) {
                    (args as any).cancel = true;
                }
                this.cdr.markForCheck();
                return;
            }
        }
        if (this.maxFilesError) {
            this.maxFilesError = '';
        }
        setTimeout(() => {
            setTimeout(() => this.updatePendingFilesCount(), 0);
        });
    }

    public async uploadFiles(): Promise<void> {
        const accessToken = await this.authService.getValidAccessToken();
        if (!accessToken) {
            this.isUploading = false;
            this.uploadTriggeredByOkButton = false;
            this.toastService.showError('Session expired. Please refresh and login again.');
            this.authService.notifySessionExpired();
            this.cdr.markForCheck();
            return;
        }

        this.uploadObj.upload(this.uploadObj.getFilesData());
    }

    public onUploadFailed: EmitType<Object> = (args: any) => {
        this.loggingService.error('Upload failed for file:', args.file?.name);
    };

    public onFileRemove(args: RemovingEventArgs): void {
        this.loggingService.info('Removing file:', args);
        this.pendingFilesCount.update((v) => v - 1);
        if (this.maxFilesError) {
            this.maxFilesError = '';
        }
    }

    onClickOk() {
        if (this.isErpUpload) {
            // Keep default behavior for ERP until a dedicated ERP uploader is used.
        }
        const hasFilesToUpload = this.pendingFilesCount() > 0;

        // Create-session flow always persists the session summary on confirm.
        // Add-files flow stays upload-only and closes once file uploads finish.
        if (this.isCreatingNewSession) {
            if (hasFilesToUpload) {
                this.isUploading = true;
                this.uploadTriggeredByOkButton = true;
                this.uploadedFilesCount = 0;
                this.totalFilesToUpload = this.uploadObj.getFilesData().length;

                this.loggingService.info(`Uploading ${this.totalFilesToUpload} files...`);
                void this.uploadFiles();
            } else {
                this.executeUpdateSession();
            }
            return;
        }

        if (hasFilesToUpload) {
            this.isUploading = true;
            this.uploadTriggeredByOkButton = true;
            this.uploadedFilesCount = 0;
            this.totalFilesToUpload = this.uploadObj.getFilesData().length;

            this.loggingService.info(`Uploading ${this.totalFilesToUpload} files...`);
            this.placeholderService.setPlaceholderCount(this.totalFilesToUpload);
            void this.uploadFiles();
        } else {
            this.appSettingsStore.setShouldRefreshTables();
            this.closeContainer();
        }
    }

    private executeUpdateSession() {
        const activeSession = this.sessionService.getActiveUploadSessionValue();
        const clientId = this.authStore.getClientValue();
        const userId   = this.authStore.getUserValue();

        if (!activeSession || !clientId || !userId) {
            this.toastService.showError('Missing session or user/client info.');
            return;
        }

        const docType = this.resolveDocType();

        this.updateSessionService.updateUploadSessionSummary(activeSession, this.notes, docType).subscribe({
            next: (response) => {
                this.toastService.showSuccess(response.message);
                this.appSettingsStore.setShouldRefreshTables();
                this.closeContainer();
            },
            error: () => {
                this.toastService.showError('Failed to update session summary.');
            },
        });
    }

    private resolveDocType(): DocType {
        return this.isSupplierStatementsSelected ? DocType.Statement : DocType.Invoice;
    }

    private resolveDocTypeFromSession(docType?: string | null): DocType {
        return docType?.trim().toLowerCase() === DocType.Statement ? DocType.Statement : DocType.Invoice;
    }

    get isAddFilesSupplierStatementsSelected(): boolean {
        if (this.isCreatingNewSession) {
            return this.isSupplierStatementsSelected;
        }

        if (this.sessionSupplierStatementsSelected) {
            return true;
        }

        const sessionDocType =
            this.sessionDocType ??
            this.newSession?.docType ??
            this.sessionService.getActiveUploadSessionValue()?.docType ??
            null;

        return this.resolveDocTypeFromSession(sessionDocType) === DocType.Statement;
    }

    onCancel() {
        /**
         * TEMPORARY (ERP modal only):
         * No persistence yet, so cancel simply closes the modal.
         */
        if (this.isErpUpload) {
            this.closeContainer();
            return;
        }
        this.closeContainer();
    }

    private closeContainer(): void {
        if (this.modal) {
            this.modal.close();
        }
    }
}
